/**
 * ai/thesisStorage.ts
 *
 * Thesis persistence adapters for MER Algo Footprints.
 *
 * Separates localStorage I/O from thesisEngine domain logic.
 * thesisEngine imports `activeThesisStorage` and calls load/save through it.
 *
 * ThesisStorage interface keeps the contract minimal:
 *   - load(): returns a raw record (may be invalid — thesisEngine validates)
 *   - save(data): persists the serialised store
 *
 * Two implementations:
 *   browserThesisStorage  — uses localStorage (default in browser)
 *   noopThesisStorage     — no-op (safe in non-browser / test environments)
 */

import { SymbolThesis } from './thesisEngine';

export const THESIS_STORAGE_KEY = 'mer_thesis_v1';

// ─── Interface ────────────────────────────────────────────────────────────────

export interface ThesisStorage {
  load(): Record<string, SymbolThesis>;
  save(data: Record<string, SymbolThesis>): void;
}

// ─── Browser implementation ───────────────────────────────────────────────────

/** Minimal runtime shape check for a persisted SymbolThesis entry. */
function isValidThesisShape(t: unknown): t is SymbolThesis {
  if (!t || typeof t !== 'object' || Array.isArray(t)) return false;
  const rec = t as Record<string, unknown>;
  return (
    typeof rec['symbol'] === 'string' &&
    typeof rec['updateCount'] === 'number' &&
    typeof rec['isStale'] === 'boolean' &&
    (rec['lastUpdatedAt'] === null || typeof rec['lastUpdatedAt'] === 'number') &&
    (rec['lastInputHash'] === null || typeof rec['lastInputHash'] === 'string') &&
    Array.isArray(rec['history'])
  );
}

export const browserThesisStorage: ThesisStorage = {
  load(): Record<string, SymbolThesis> {
    try {
      const raw = localStorage.getItem(THESIS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      // Minimal shape validation: must be a plain object
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
      // Filter out any entries that fail per-entry shape validation
      const validated: Record<string, SymbolThesis> = {};
      for (const [sym, entry] of Object.entries(parsed as Record<string, unknown>)) {
        if (isValidThesisShape(entry)) validated[sym] = entry;
      }
      return validated;
    } catch {
      return {};
    }
  },

  save(data: Record<string, SymbolThesis>): void {
    try {
      localStorage.setItem(THESIS_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Storage quota or SSR — silently ignore
    }
  },
};

// ─── No-op implementation (tests / SSR) ──────────────────────────────────────

export const noopThesisStorage: ThesisStorage = {
  load(): Record<string, SymbolThesis> { return {}; },
  save(_data: Record<string, SymbolThesis>): void { /* intentional no-op */ },
};

// ─── Active adapter ───────────────────────────────────────────────────────────
// Defaults to browser storage; replace with noopThesisStorage in non-browser envs.

export let activeThesisStorage: ThesisStorage =
  typeof localStorage !== 'undefined' ? browserThesisStorage : noopThesisStorage;

/** Override the active storage adapter (e.g. for testing). */
export function setThesisStorage(adapter: ThesisStorage): void {
  activeThesisStorage = adapter;
}
